import { fieldTypes } from '@constants/constant';
import { Application } from '@enums/application.enums';

export const CREATE_PLAN_CONSTANTS = {
  FREE_DMS_LABEL: 'Number of Free Direct Messages per Month Included In Plan',
  FREE_DMS_INFO_MESSAGE:
    'If a Fan or Creator purchases this plan, they will have access to this number of free direct messages per month. After the free direct messages are used, they will need to pay for each direct message. The price of each individual direct message can be set in Preferences > Monetization or editing the Price below.',
  SET_ATLEAST_PRICE_ONE_FOR_FREE_DMS:
    'To enable Free Included Direct Messages for this Plan, please set your "Price to send an Individual Direct Message" to be at least $1.00.',
  AT_LEAST_ONE_DURATION_REQUIRED:
    'At least one duration (1, 3, 6, or 12 months) must be selected.',
  PRICE_ONE_MONTH_REQUIRED_FOR_DISCOUNT:
    'One Month Price is required if Duration Discounts are enabled.',
  INCLUDE_EXPERIENCE_IN_PLAN: 'Include Free Experiences in Plan Duration',
  INCLUDE_EXPERIENCE_IN_PLAN_HINT:
    'Select optional free Experiences and respective Options to include in this subscription plan duration. You can also select how many times a Subscriber can redeem the Experience per month (e.g., Two 30 min coaching sessions per month). Subscriber will receive new codes to redeem this Experience each month, so long as they are an active subscriber, but they must use codes within the month or it expires. For example, if a fan pre-pays for 3 months of a subscription, they will receive new codes for 3 months.',
  INCLUDE_MERCHANDISE_IN_PLAN: 'Include One Free Merchandise Item Per Duration Term',
  INCLUDE_MERCHANDISE_IN_PLAN_HINT:
    'Select optional Merchandise to include in this subscription plan duration. Subscriber will receive a code to redeem one free item from selected merchandise items. Subscriber still pays for Shipping & Taxes. Important: A new code is provided upon renewal of the term, so if you only want yearly subscribers to receive a free item, don’t enable this for 1, 3, and 6 month terms.',
  SELECT_EXPERIENCES_TITLE: 'Select Experiences & Options *',
  SELECT_MERCHANDISE_TITLE: 'Select Redeemable Merchandise Items. Fan can choose only one item per term. *',
  SELECT_DURATION_OPTION: 'Select Option *',
  NO_EXPERIENCES_FOUND: 'No Experiences Found. Please add an Experience first.',
  YES: 'Yes',
  NO: 'No',
  DURATION_OPTIONS: 'Experience Options:',
  HIDE_DETAILS: 'Hide Details',
  SHOW_DETAILS: 'Show Details',
  DETAILS: 'Details',
  SHOW: 'Show',
  HIDE: 'Hide',
  DISCOUNT_CODE: 'Discount Code:',
  REDEMPTION_CODE: 'Redemption Code:',
  TERMS_CONDITIONS: 'Terms & Conditions:',
  PAYMENT_SUCCESSFUL: 'Payment Successful',
  YOUR_SUBSCRIPTION_INCLUDES:
    'Your subscription includes the following items. You will enter your unique code at checkout to redeem the item for free.',
  MONTHLY_BENEFIT_RENEWAL: 'Benefit Renewal',
  GO_TO_STORE: 'GO TO STORE',
  EXIT: 'EXIT',
  FREE_MERCHANDISE_ITEM: 'FREE Merchandise',
  FREE_EXPERIENCE: 'FREE Experience',
  FREE_EXPERIENCES: 'FREE Experiences',
  NO_MERCHANDISE_FOUND: 'No Merchandise found. Please add a Merchandise first.',
  INCLUDED_EXPERIENCES_MERCHANDISE: 'INCLUDED EXPERIENCES & MERCHANDISE',
  EXPERIENCE_AVAIL_COUNT_LABEL: 'Number of Included Free Experiences Each Month',
  EXPERIENCE_AVAIL_COUNT_PLACEHOLDER: 'Enter Times a Fan Can Avail Free',
  USE_LEFT: 'Use Left Before',
  USES_LEFT: 'Uses Left Before',
  VALID_TILL: 'Valid Till',
  SHOW_CODE: 'Show Code',
  HIDE_CODE: 'Hide Code',
  DURATION_PACKAGES_TITLE: 'Duration Packages',
  ACTIVE: 'Active',
  INCLUDED: 'Included',
  NOT_INCLUDED: 'Not Included',
  ONE_MONTH: '1 Month',
  THREE_MONTH: '3 Month',
  SIX_MONTH: '6 Month',
  TWELVE_MONTH: '12 Month',
  DISCOUNT_LABEL: 'Duration Discount from 1 Month Price',
  TOTAL_PRICE_LABEL: 'Total Price for Duration',
  TRIAL_PERIOD_LABEL: 'Trial Period (days)',
  EXPERIENCE_LABEL: 'EXPERIENCE',
  MERCHANDISE_LABEL: 'MERCHANDISE',
  SELECT_EXPERIENCE_HINT:
    'Select one experience and a duration option for this tier',
  DURATION_OPTIONS_COUNT: 'duration options',
  EXPERIENCE_CHIP: 'Experience',
  EXPERIENCES_CHIP: 'Experiences',
  MERCHANDISE_CHIP: 'Merchandise',
  MERCHANDISES_CHIP: 'Merchandise Items',
  HAS_EXPERIENCE: 'Has experience',
  HAS_MERCHANDISE: 'Has merchandise',
  NO_EXPERIENCE: 'No Experience Added',
  NO_MERCHANDISE: 'No Merchandise Added',
  ACTIVE_CAP: 'ACTIVE',
  PLAN_BENEFITS: 'Plan Benefits',
  INCLUDED_IN_TIER: 'Included in this tier',
  REGULAR_PRICE: 'Regular Price',
  SELECT_OPTION_FAN_CAN_AVAIL: 'Select which Experience Options Fan can Redeem for Free *',
  FREE_MERCHANDISE: 'Free Merchandise',
  ONE_PER_TERM: '1 Per Term',
  MERCHANDISE_OPTIONS: 'Included Merchandise Options:',
  EXPERIENCE_OPTIONS: 'Included Experience Options:',
  INCLUDED_EXPERIENCE: 'Included Experience',
  INCLUDED_MERCHANDISE: 'Included Merchandise',
} as const;

export const predefinedDMOptions = [
  {
    label: 'Zero',
    value: 0
  },
  {
    label: '5',
    value: 5
  },
  {
    label: '10',
    value: 10
  },
  {
    label: '25',
    value: 25
  },
  {
    label: '50',
    value: 50
  },
  {
    label: '100',
    value: 100
  },
  {
    label: 'Unlimited',
    value: -1
  }
];

export const getFeaturesField = (callback: Function) => {
  return {
    type: fieldTypes.CUSTOM_MULTISELECT,
    label: 'Plan Benefits',
    name: 'features',
    placeholder: 'Select',
    class: 'col-12',
    labelKey: 'label',
    valueKey: 'value',
    options: [
      {
        label: 'Exclusive Photos',
        name: 'exclusivePhotos',
        value: 'Exclusive Photos'
      },
      {
        label: 'Exclusive Videos',
        name: 'exclusiveVideos',
        value: 'Exclusive Videos'
      },
      {
        label: 'Exclusive Podcasts',
        name: 'exclusivePodcasts',
        value: 'Exclusive Podcasts'
      },
      {
        label: 'All E-Courses are FREE',
        name: 'allECoursesAreFree',
        value: 'All E-Courses are FREE'
      },
      {
        label: 'Money Saving VIP Offers from my Partners',
        name: 'moneySavingVipOffersFromMyPartners',
        value: 'Money Saving VIP Offers from my Partners'
      },
      {
        label: 'Chat Room Access',
        name: 'chatRoomAccess',
        value: 'Chat Room Access'
      }
    ],
    customMultiselectConfig: {
      defaultLabel: 'Select Benefits',
      filterLabel: 'Select Benefits',
      optionLabel: 'label',
      customButtonLabel: 'Add New Benefit',
      showHeader: true
    },
    callback,
    validation: {
      required: { message: 'Plan Benefits is required' }
    }
  };
};

export const ADD_FEATURE_MODAL_LABELS = {
  HEADER_ADD: 'Add New Benefit',
  BODY: 'Benefit Name',
  ADD_BENEFIT: 'Add Benefit',
  EDIT_BENEFIT: 'Edit Benefit',
  CANCEL: 'Cancel'
};

export const termsAndConditionsSubExperience = [
  'Codes are unique and not transferrable.',
  'Must be redeemed by expiration date.'
];

export const termsAndConditionsSubMerchandise = [
  'Shipping costs & taxes are not included.',
  'Codes are unique and not transferrable.',
  'Must be redeemed by expiration date.'
];

export const merchandiseField = {
  type: fieldTypes.CUSTOM_MULTISELECT,
  label: 'Select Merchandise',
  name: 'included_merchandise',
  placeholder: 'Select',
  class: 'col-12',
  labelKey: 'label',
  valueKey: 'sk',
  options: [],
  customMultiselectConfig: {
    defaultLabel: 'Select',
    selectAllButtonLabel: 'Select All',
    filterLabel: 'Search',
    optionLabel: 'label',
    showHeader: true,
    optionIconLabel: 'image',
    optionPrice: 'price',
    optionSubLabel: 'from',
    useCardView: true,
    dataKey: 'sk'
  },
  validation: {
    required: { message: 'Select Merchandise is required' }
  }
};

export const trialField: any = {
  type: fieldTypes.NUMBER,
  label: 'Optional Free Trial Period',
  placeholder: 'Enter Number Of Days',
  name: 'free_trial_day',
  class: 'col-12',
  styleClass: 'mt-2',
  validation: {
    max: {
      value: Application.NINETY,
      message: 'Maximum 90 days are allowed'
    },
    pattern: {
      message: 'Only numeric values are allowed'
    }
  }
};

export const trial3MonthField: any = {
  type: fieldTypes.NUMBER,
  label: 'Optional Free Trial Period',
  placeholder: 'Enter Number Of Days',
  name: 'free_trial_3day',
  class: 'col-12',
  styleClass: 'mt-2',
  validation: {
    max: {
      value: Application.NINETY,
      message: 'Maximum 90 days are allowed'
    },
    pattern: {
      message: 'Only numeric values are allowed'
    }
  }
};

export const trial6MonthField: any = {
  type: fieldTypes.NUMBER,
  label: 'Optional Free Trial Period',
  placeholder: 'Enter Number Of Days',
  name: 'free_trial_6day',
  class: 'col-12',
  styleClass: 'mt-2',
  validation: {
    max: {
      value: Application.NINETY,
      message: 'Maximum 90 days are allowed'
    },
    pattern: {
      message: 'Only numeric values are allowed'
    }
  }
};

export const trial12MonthField: any = {
  type: fieldTypes.NUMBER,
  label: 'Optional Free Trial Period',
  placeholder: 'Enter Number Of Days',
  name: 'free_trial_12day',
  class: 'col-12',
  styleClass: 'mt-2',
  validation: {
    max: {
      value: Application.NINETY,
      message: 'Maximum 90 days are allowed'
    },
    pattern: {
      message: 'Only numeric values are allowed'
    }
  }
};

export const getExperienceAvailField = (
  tier: string,
  experienceSk: string
): any => ({
  type: fieldTypes.NUMBER,
  label: CREATE_PLAN_CONSTANTS.EXPERIENCE_AVAIL_COUNT_LABEL,
  placeholder: CREATE_PLAN_CONSTANTS.EXPERIENCE_AVAIL_COUNT_PLACEHOLDER,
  name: `exp_avail_${tier}_${experienceSk}`,
  class: 'col-12',
  styleClass: 'mt-2',
  validation: {
    required: { message: 'This field is required' },
    min: {
      value: Application.ONE,
      message: 'Minimum value is 1'
    },
    max: {
      value: Application.TEN,
      message: 'Maximum value is 50'
    },
    pattern: {
      message: 'Only numeric values are allowed'
    }
  }
});
